import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Download, Ticket } from "lucide-react";
import { api } from "@/lib/api";
import { mediaSrc } from "@/components/HeroVideo";

export default function PurchaseSuccessPage() {
  const [status, setStatus] = useState("checking");
  const [p, setP] = useState(null);

  useEffect(() => {
    const sid = new URLSearchParams(window.location.search).get("session_id");
    if (!sid) return setStatus("error");
    let tries = 0;
    const poll = async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/purchases/status/${sid}`);
        setP(data);
        if (data.payment_status === "paid") {
          if (data.kind === "sponsor_deposit" && !data.deck_url && tries < 10) return setTimeout(poll, 2000);
          if (data.kind === "product" && !data.download_url && tries < 10) return setTimeout(poll, 2000);
          return setStatus("paid");
        }
      } catch (e) { /* retry */ }
      if (tries >= 10) return setStatus("pending");
      setTimeout(poll, 2000);
    };
    poll();
  }, []);

  const sponsor = p?.kind === "sponsor_deposit";
  if (status === "paid" && p?.kind === "product") return (
    <div className="mx-auto max-w-xl px-4 py-24 text-center" data-testid="purchase-success-page">
      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-500/20 text-3xl text-green-400">✓</div>
      <span className="eyebrow mt-6 block">{p.product_kind === "download" ? "Purchase complete" : "Engagement booked"}</span>
      <h1 className="mt-2 font-display text-4xl uppercase text-chrome" data-testid="purchase-title">{p.product_name}</h1>
      <p className="mt-3 text-neutral-400">{p.name} · ${p.amount.toLocaleString()}</p>
      <div className="mt-2 font-display text-2xl tracking-wider text-gold" data-testid="purchase-reference">{p.reference_code}</div>
      <p className="mt-4 text-sm text-neutral-500">{p.product_kind === "download" ? "Your PDF is ready below and a copy of this link was emailed to you." : "Your engagement brief is below. Reply to the confirmation email with 3 time slots to schedule kickoff."}</p>
      {p.download_url && (
        <a href={mediaSrc(p.download_url)} target="_blank" rel="noreferrer" data-testid="product-download-btn" className="btn-gold mt-8 inline-flex items-center gap-2 rounded-full px-8 py-4 font-display text-lg uppercase">
          <Download className="h-5 w-5" /> {p.product_kind === "download" ? "Download PDF" : "Download Engagement Brief"}
        </a>
      )}
      <div className="mt-8"><Link to="/products" className="font-cond font-bold uppercase text-neutral-400 hover:text-white">More products</Link></div>
    </div>
  );
  return (
    <div className="mx-auto max-w-xl px-4 py-24 text-center" data-testid="purchase-success-page">
      {status === "checking" && (<><Loader2 className="mx-auto h-12 w-12 animate-spin text-cyan-500" /><h1 className="mt-6 font-display text-3xl uppercase text-chrome">Confirming payment…</h1>
        {sponsor && <p className="mt-2 text-neutral-400">Building your branded sponsor deck.</p>}</>)}
      {status === "paid" && p && (
        <>
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-500/20 text-3xl text-green-400">✓</div>
          <span className="eyebrow mt-6 block">{sponsor ? "Tier reserved" : p.kind === "proposal_deposit" ? "Deposit received" : "Booth confirmed"}</span>
          <h1 className="mt-2 font-display text-4xl uppercase text-chrome" data-testid="purchase-title">{sponsor ? `${p.tier_name} Sponsorship` : p.kind === "proposal_deposit" ? "Date Locked" : p.booth_name}</h1>
          <p className="mt-3 text-neutral-400">{sponsor ? `${p.company} · deposit $${p.amount.toLocaleString()} of $${p.tier_price.toLocaleString()}` : p.kind === "proposal_deposit" ? `${p.company || ""} · ${p.lane_name} · deposit $${p.amount.toLocaleString()} · ${p.request_reference}` : `${p.business_name} · ${p.event_title}`}</p>
          <div className="mt-2 font-display text-2xl tracking-wider text-gold" data-testid="purchase-reference">{p.reference_code}</div>
          <p className="mt-4 text-sm text-neutral-500">A confirmation email is on its way.</p>
          {sponsor && p.deck_url && (
            <a href={mediaSrc(p.deck_url)} target="_blank" rel="noreferrer" data-testid="deck-download-btn"
              className="btn-gold mt-8 inline-flex items-center gap-2 rounded-full px-8 py-4 font-display text-lg uppercase">
              <Download className="h-5 w-5" /> Download Sponsor Deck (PDF)
            </a>
          )}
          <div className="mt-8"><Link to="/events" className="inline-flex items-center gap-2 font-cond font-bold uppercase text-neutral-400 hover:text-cyan-400"><Ticket className="h-4 w-4" /> Browse events</Link></div>
        </>
      )}
      {status === "pending" && <><h1 className="font-display text-3xl uppercase text-chrome">Payment processing</h1><p className="mt-3 text-neutral-400">We'll email you as soon as it clears. Reference: <span className="text-gold">{p?.reference_code}</span></p></>}
      {status === "error" && <h1 className="font-display text-3xl uppercase text-red-400">Missing session</h1>}
    </div>
  );
}
